import React, { useEffect, useState, useMemo } from 'react';
import {
  collection, query, orderBy, limit, onSnapshot,
  doc, deleteDoc, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { useIsAdmin } from '../auth/useAdmin';
import './AdminBugReports.css';

function formatDate(ts) {
  if (!ts) return '—';
  // Firestore Timestamp
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseBrowser(ua) {
  if (!ua) return '—';
  if (/iPhone|iPad/.test(ua)) return 'iOS ' + (ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '');
  if (/Android/.test(ua)) return 'Android ' + (ua.match(/Android ([\d.]+)/)?.[1] || '');
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

function AdminBugReports() {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'bug_reports'),
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (e) => {
        console.error('Admin bug report load failed:', e);
        setErr(e.message || 'Failed to load reports');
        setLoading(false);
      }
    );
    return unsub;
  }, [isAdmin]);

  const counts = useMemo(() => {
    const byPage = {};
    for (const r of reports) {
      const k = r.page || 'unknown';
      byPage[k] = (byPage[k] || 0) + 1;
    }
    return byPage;
  }, [reports]);

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'bug_reports', id));
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Failed to delete report.');
    }
  };

  const handleClearAll = async () => {
    if (!reports.length) return;
    setClearing(true);
    try {
      // Firestore batch caps at 500 writes
      const chunks = [];
      for (let i = 0; i < reports.length; i += 400) {
        chunks.push(reports.slice(i, i + 400));
      }
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((r) => batch.delete(doc(db, 'bug_reports', r.id)));
        await batch.commit();
      }
      setConfirmClearAll(false);
    } catch (e) {
      console.error('Clear all failed:', e);
      alert('Failed to clear all reports.');
    } finally {
      setClearing(false);
    }
  };

  if (!user) {
    return (
      <div className="admin-bugs-gate">
        <h2>Admin only</h2>
        <p>Sign in with the admin account to view bug reports.</p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="admin-bugs-gate">
        <h2>Not authorized</h2>
        <p>This page is restricted to the admin account.</p>
      </div>
    );
  }

  return (
    <div className="admin-bugs">
      <header className="admin-bugs-head">
        <div>
          <div className="admin-bugs-kicker">Admin · Feedback inbox</div>
          <h1 className="admin-bugs-title">Bug reports</h1>
          <p className="admin-bugs-sub">
            {loading ? 'Loading…' : (
              <>
                <strong>{reports.length}</strong> open report{reports.length === 1 ? '' : 's'}
                {reports.length > 0 && (
                  <> · by page: {Object.entries(counts).map(([k, v]) => (
                    <span key={k} className="admin-bugs-pagetag">{k} <em>{v}</em></span>
                  ))}</>
                )}
              </>
            )}
          </p>
        </div>
        {reports.length > 0 && (
          <div className="admin-bugs-clear">
            {!confirmClearAll ? (
              <button className="admin-bugs-btn danger-ghost" onClick={() => setConfirmClearAll(true)}>
                Clear all
              </button>
            ) : (
              <>
                <button className="admin-bugs-btn ghost" onClick={() => setConfirmClearAll(false)} disabled={clearing}>
                  Cancel
                </button>
                <button className="admin-bugs-btn danger" onClick={handleClearAll} disabled={clearing}>
                  {clearing ? 'Clearing…' : `Delete all ${reports.length}`}
                </button>
              </>
            )}
          </div>
        )}
      </header>

      {err && <div className="admin-bugs-error">{err}</div>}

      {!loading && reports.length === 0 && !err && (
        <div className="admin-bugs-empty">
          <div className="admin-bugs-empty-icon">✓</div>
          <h2>Inbox zero</h2>
          <p>No open bug reports. Nice.</p>
        </div>
      )}

      <div className="admin-bugs-list">
        {reports.map((r) => {
          const isOpen = expandedId === r.id;
          return (
            <article key={r.id} className={`admin-bugs-card ${isOpen ? 'open' : ''}`}>
              <div className="admin-bugs-card-head">
                <div className="admin-bugs-card-meta">
                  <span className="admin-bugs-when">{formatDate(r.createdAt)}</span>
                  <span className="admin-bugs-dot">·</span>
                  <span className="admin-bugs-page">{r.page || 'unknown'}</span>
                  <span className="admin-bugs-dot">·</span>
                  <span className="admin-bugs-browser">{parseBrowser(r.userAgent)}</span>
                  {r.email && (
                    <>
                      <span className="admin-bugs-dot">·</span>
                      <a className="admin-bugs-email" href={`mailto:${r.email}?subject=Re: PK Champs Coach bug report`}>
                        {r.email}
                      </a>
                    </>
                  )}
                </div>
                <div className="admin-bugs-card-actions">
                  <button
                    className="admin-bugs-btn ghost small"
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                  >
                    {isOpen ? 'Hide details' : 'Details'}
                  </button>
                  <button
                    className="admin-bugs-btn danger small"
                    onClick={() => handleDelete(r.id)}
                    title="Mark addressed & delete"
                  >
                    Resolve
                  </button>
                </div>
              </div>

              <p className="admin-bugs-message">{r.message}</p>

              {isOpen && (
                <dl className="admin-bugs-details">
                  <div><dt>URL</dt><dd><a href={r.url} target="_blank" rel="noopener noreferrer">{r.url}</a></dd></div>
                  <div><dt>User id</dt><dd>{r.uid || <em>anonymous</em>}</dd></div>
                  <div><dt>Viewport</dt><dd>{r.viewport || '—'}</dd></div>
                  <div><dt>App version</dt><dd>{r.appVersion || '—'}</dd></div>
                  <div className="full"><dt>User agent</dt><dd className="mono">{r.userAgent || '—'}</dd></div>
                </dl>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

export default AdminBugReports;
